import React from 'react';
import { cn } from '@restworld/utils-common';

export const ContentContainer = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('p-4 sm:p-5 flex flex-col gap-4 w-full', className)} {...props} />
);

export const Card = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('bg-white rounded-xl border border-neutral-40 overflow-hidden min-w-0', className)} {...props} />
);

// --- Card 1: Tabs + Timeline + Period cost ---

export const TabContainer = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex border-b border-neutral-40 py-2', className)} {...props} />
);

export const TabButton = ({
  isActive,
  className,
  ...props
}: { isActive: boolean } & React.ButtonHTMLAttributes<HTMLButtonElement>) => (
  <button
    className={cn(
      'px-4 py-2.5 border-none bg-transparent text-sm font-semibold cursor-pointer font-[inherit]',
      isActive
        ? 'text-neutral-900 border-b-2 border-neutral-900 -mb-px'
        : 'text-neutral-200 border-b-2 border-transparent -mb-px',
      className
    )}
    {...props}
  />
);

export const TimelineScrollContainer = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('overflow-x-auto max-w-full', className)} {...props} />
);

export const TimelineInner = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('px-4 pt-4 pb-6', className)} {...props} />
);

export const MonthHeaderCell = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('text-xs font-semibold text-neutral-700 pb-1 text-left px-1', className)} {...props} />
);

export const DayHeaderCell = React.forwardRef<
  HTMLDivElement,
  { isToday: boolean; isFuture: boolean } & React.HTMLAttributes<HTMLDivElement>
>(({ isToday, isFuture, className, style, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'text-center text-xs pb-2 font-sans',
      isToday ? 'font-bold text-neutral-900' : 'font-medium text-neutral-200',
      isFuture && 'text-neutral-40',
      className
    )}
    style={{
      borderBottom: isToday ? '2px solid currentColor' : '1px solid #DEDEE1',
      ...style,
    }}
    {...props}
  />
));

export const JobRow = ({
  $isLast,
  $isFirst,
  className,
  ...props
}: { $isLast?: boolean; $isFirst?: boolean } & React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('flex items-center', $isFirst && 'pt-6', !$isLast && 'border-b border-neutral-40', className)}
    {...props}
  />
);

export const JobLabel = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'py-3 flex items-center gap-2 flex-shrink-0 bg-white z-10 sticky left-0 w-[156px] pl-4 -ml-4',
      className
    )}
    {...props}
  />
);

export const JobDot = ({
  color,
  className,
  style,
  ...props
}: { color: string } & React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('w-2 h-2 rounded-full flex-shrink-0', className)}
    style={{ background: color, ...style }}
    {...props}
  />
);

export const JobName = ({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) => (
  <span
    className={cn(
      'text-sm font-medium text-neutral-700 overflow-hidden whitespace-nowrap text-ellipsis max-w-[120px]',
      className
    )}
    {...props}
  />
);

export const BarCell = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('h-9 flex items-center justify-center relative', className)} {...props} />
);

export const Bar = ({
  color,
  isHovered,
  roundLeft,
  roundRight,
  className,
  style,
  ...props
}: {
  color: string;
  isHovered: boolean;
  roundLeft: boolean;
  roundRight: boolean;
} & React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('w-full transition-opacity duration-100', className)}
    style={{
      height: '22px',
      background: color,
      opacity: isHovered ? 1 : 0.8,
      borderRadius: `${roundLeft ? 5 : 0}px ${roundRight ? 5 : 0}px ${roundRight ? 5 : 0}px ${roundLeft ? 5 : 0}px`,
      marginLeft: roundLeft ? '2px' : '0',
      marginRight: roundRight ? '2px' : '0',
      ...style,
    }}
    {...props}
  />
);

export const TooltipContent = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col gap-1', className)} {...props} />
);

export const TooltipDate = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('font-semibold mb-1 text-xs', className)} {...props} />
);

export const TooltipJobItem = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex items-center gap-1.5 mb-0.5 text-xs [&_span]:overflow-hidden [&_span]:text-ellipsis [&_span]:[display:-webkit-box] [&_span]:[-webkit-line-clamp:2] [&_span]:[-webkit-box-orient:vertical] [&_span]:break-words',
      className
    )}
    {...props}
  />
);

export const TooltipCost = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('mt-1 pt-1 font-semibold text-xs border-t border-[rgba(255,255,255,0.15)]', className)}
    {...props}
  />
);

export const PeriodCostRow = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('px-4 py-3.5 border-t border-neutral-40 flex items-center justify-between', className)}
    {...props}
  />
);

export const PeriodCostLabel = ({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) => (
  <span className={cn('text-sm text-neutral-900 font-medium', className)} {...props} />
);

export const PeriodCostValue = ({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) => (
  <span className={cn('text-lg font-medium text-neutral-900', className)} {...props} />
);

export const EmptyState = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('py-6 text-center text-neutral-200 text-sm', className)} {...props} />
);

// --- Card 2: All-time summary ---

export const SectionTitle = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('px-4 py-3.5 border-b border-neutral-40', className)} {...props} />
);

export const SectionTitleText = ({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) => (
  <span className={cn('text-sm font-bold text-neutral-900', className)} {...props} />
);

export const SectionSubtitle = ({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) => (
  <span className={cn('text-sm text-neutral-200', className)} {...props} />
);

export const SummaryItemsContainer = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('py-2 min-h-[420px]', className)} {...props} />
);

export const SummaryItem = ({
  hasBorder,
  className,
  ...props
}: { hasBorder: boolean } & React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('px-4 flex items-center gap-3 py-3', hasBorder && 'border-b border-neutral-40', className)}
    {...props}
  />
);

export const SummaryItemInfo = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex-1 min-w-0', className)} {...props} />
);

export const SummaryItemDate = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('text-xs text-neutral-200 mt-0.5', className)} {...props} />
);

export const SummaryItemStats = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('text-right flex-shrink-0', className)} {...props} />
);

export const SummaryItemDays = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('text-lg font-bold text-neutral-900 leading-none', className)} {...props} />
);

export const SummaryItemDaysLabel = ({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) => (
  <span className={cn('text-xs font-medium text-neutral-900', className)} {...props} />
);

export const SummaryItemCost = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('text-sm text-neutral-200 mt-1', className)} {...props} />
);

export const PaginationContainer = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('px-4 py-2.5 border-t border-neutral-40 flex items-center justify-between', className)}
    {...props}
  />
);

export const PaginationInfo = ({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) => (
  <span className={cn('text-xs text-neutral-200', className)} {...props} />
);

export const PaginationButtons = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex gap-1', className)} {...props} />
);

export const PaginationButton = ({
  isDisabled,
  className,
  ...props
}: { isDisabled: boolean } & React.ButtonHTMLAttributes<HTMLButtonElement>) => (
  <button
    className={cn(
      'w-8 h-8 rounded-lg border border-neutral-40 bg-white flex items-center justify-center',
      isDisabled ? 'cursor-not-allowed opacity-30' : 'cursor-pointer opacity-100',
      className
    )}
    {...props}
  />
);
